import { Navigate } from 'react-router-dom';
import { useApi } from 'api/api-context';
import { BOOKING_LOGIN_PATH } from 'routes/paths';
import { useEffect, useState } from 'react';
import useLang from 'lang';

/**
 * Redirects unauthenticated users to the booking login page.
 */
export default function BookingGuard({ children }: { children: React.ReactNode }) {
  const { jwt, request, destroySession } = useApi();
  const { _t } = useLang();
  const [valid, setValid] = useState(false);
  const [checking, setChecking] = useState(Boolean(jwt));

  useEffect(() => {
    let cancelled = false;
    if (!jwt) {
      setValid(false);
      setChecking(false);
      return;
    }
    setChecking(true);
    // Validate against a booking endpoint available to both Phillip users and booking guests.
    request({ method: 'GET', url: '/reservations/spaces' })
      .then(() => {
        if (!cancelled) setValid(true);
      })
      .catch((error) => {
        if (!cancelled) {
          if ([401, 403].includes(error?.response?.status)) {
            destroySession();
            setValid(false);
          } else {
            // A transient server error must not erase an otherwise valid session.
            setValid(true);
          }
        }
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, request, destroySession]);

  if (!jwt || (!checking && !valid)) {
    return <Navigate to={BOOKING_LOGIN_PATH.original} replace />;
  }

  if (checking) return <p>{_t('Loading...')}</p>;

  return <>{children}</>;
}
